import {
  Box,
  Typography,
  TextField,
  Grid,
  Button,
  CircularProgress,
  Alert,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Divider,
} from "@mui/material";
import React, { useState, useEffect, useContext } from "react";
import axios from "axios";
import { API_PATH, appContext } from "../../../App";
import { getAxiosConfig } from "../../../helpers/AuthHelper";
import MiningResultModal from "./MiningResultModal";
import MachinesResultModal from "./MachinesResultModel";
import { getCryptoInfo } from "../../utils/cryptoUtils";
import SearchByBatchModal from "./SearchByBatchModal";

interface MachineModelInfo {
  id: number;
  modelName: string;
  power: number;
  hashRate: number;
  name?: string; // for JSX display
}

export default function PricePredictionController() {
  const [form, setForm] = useState({
    machineHashrateTh: "200",
    powerWatts: "3000",
    uptimePercent: 99,
    hostingRateUsdPerKwh: "0.06",
    currentBtcPriceUsd: "",
    monthlyBtcPriceIncreasePercent: "2.5",
    monthlyDifficultyIncreasePercent: "4",
    halvingDifficultyDecreasePercent: "25",
    targetReturnPercent: "50",
    difficulty: "",
  });

  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [error, setError] = useState("");
  const [modalOpen, setModalOpen] = useState(false);

  const [machinesResponseLoading,setMachinesResponseLoading] =useState(false);
  const [machineResult, setMachineResult] = useState<any[]>([])
  const [machineModalOpen, setMachineModalOpen] = useState(false);

  // Multiple machines state
  const [useMultipleMachines, setUseMultipleMachines] = useState(true);
  const [allMachines, setAllMachines] = useState<MachineModelInfo[]>([]);

  const [selectedMachines, setSelectedMachines] = useState<any[]>([]);
  const { authRole } = useContext(appContext);

  useEffect(() => {
    async function loadCryptoData() {
      try {
        const { priceUsd, difficulty } = await getCryptoInfo("btc");

        setForm(prev => ({
          ...prev,
          currentBtcPriceUsd: String(priceUsd),
          difficulty: String(difficulty),
        }));
      } catch (err) {
        console.error("Failed to load crypto info", err);
      }
    }

    loadCryptoData();
  }, []);

  // Start from the same assumptions the daily price prediction uses
  useEffect(() => {
    async function loadPredictionSettings() {
      try {
        const { data } = await axios.get(`${API_PATH}/price-prediction-settings`, getAxiosConfig());
        setForm(prev => ({
          ...prev,
          monthlyBtcPriceIncreasePercent: String(data.monthlyBtcPriceIncreasePercent),
          monthlyDifficultyIncreasePercent: String(data.monthlyDifficultyIncreasePercent),
          halvingDifficultyDecreasePercent: String(data.halvingDifficultyDecreasePercent),
          targetReturnPercent: String(data.targetReturnPercent),
          uptimePercent: data.uptimePercent,
        }));
      } catch (err) {
        console.error("Failed to load price prediction settings", err);
      }
    }

    loadPredictionSettings();
  }, []);



  useEffect(() => {
    if (useMultipleMachines) fetchMachineList();
  }, [useMultipleMachines]);

  const fetchMachineList = async () => {
    try {
      const response = await axios.get(`${API_PATH}/machine_model/list`, getAxiosConfig());
      const machines = response.data
      .filter((item: any) => item.coin?.coin === "BTC"  && item.power > 50)
        .map((item: any) => ({
          id: item.id,
          modelName: item.modelName,
          power: item.power,
          hashRate: item.hashRate,
          name: item.modelName,
        }));
      setAllMachines(machines);
    } catch (error) {
      console.error("Error fetching machine list:", error);
    }
  };

  const handleChange = (e: any) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError("");
    setResult(null);

    try {
      const body = Object.keys(form).reduce((acc: any, key) => { acc[key] = parseFloat((form as any)[key]); return acc; }, {});

      const response = await axios.post(API_PATH + "/mining/simulate", body, getAxiosConfig());
      setResult(response.data);
      setModalOpen(true);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const handleMachinesDataSubmit = async () => {
    setMachinesResponseLoading(true);
    setError("");
    setResult(null);

    try {
      const machineIds = selectedMachines.map((m) => m.id);

      const body = {
        machineIds,
        hostingRateUsdPerKwh: parseFloat(form.hostingRateUsdPerKwh as any),
        currentBtcPriceUsd: parseFloat(form.currentBtcPriceUsd as any),
        uptimePercent: parseFloat(form.uptimePercent as any),
        monthlyBtcPriceIncreasePercent: parseFloat(form.monthlyBtcPriceIncreasePercent as any),
        monthlyDifficultyIncreasePercent: parseFloat(form.monthlyDifficultyIncreasePercent as any),
        halvingDifficultyDecreasePercent: parseFloat(form.halvingDifficultyDecreasePercent as any),
        targetReturnPercent: parseFloat(form.targetReturnPercent as any),
        networkHashrateTh: parseFloat(form.machineHashrateTh as any),
        difficulty: parseFloat(form.difficulty as any),
      };

      const response = await axios.post(
        API_PATH + "/mining/simulate/machines",
        body,
        getAxiosConfig()
      );

      setMachineResult(response.data);
      setMachineModalOpen(true);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || "Something went wrong");
    } finally {
      setMachinesResponseLoading(false);
    }
  };


  const fieldGroups = [
    {
      title: "Mining Hardware Specifications",
      description: "Configure your mining hardware parameters",
      fields: [
        { key: "machineHashrateTh", label: "Machine Hashrate (TH/s)", helper: "Mining power in Terahashes per second" },
        { key: "powerWatts", label: "Machine Power (W)", helper: "Power consumption in watts" },
        { key: "uptimePercent", label: "Uptime Percentage (%)", helper: "Expected operational uptime" }
      ],
      requiredRole: null,
    },
    {
      title: "Operating Costs",
      description: "Define your operational expenses",
      fields: [
        { key: "hostingRateUsdPerKwh", label: "Hosting Cost ($/kWh)", helper: "Electricity cost per kilowatt-hour" }
      ],
      requiredRole: null,
    },
    {
      title: "Market Projections",
      description: "Set your market expectations and forecasts",
      fields: [
        { key: "currentBtcPriceUsd", label: "Initial BTC Price ($)", helper: "Starting Bitcoin price in USD" },
        { key: "monthlyBtcPriceIncreasePercent", label: "Monthly BTC Price Increase (%)", helper: "Expected monthly BTC price growth", adminOnly: true },
        { key: "monthlyDifficultyIncreasePercent", label: "Monthly Difficulty Increase (%)", helper: "Expected monthly mining difficulty growth", adminOnly: true },
        { key: "halvingDifficultyDecreasePercent", label: "Halving Month Difficulty Decrease (%)", helper: "One-time difficulty drop in each halving month, on top of the monthly increase", adminOnly: true },
        { key: "difficulty", label: "Current Difficulty", helper: "Network mining difficulty" }
      ],
      requiredRole: null,
    },
    {
      title: "Business Target",
      description: "Set your profitability goals",
      fields: [
        { key: "targetReturnPercent", label: "Target Profit Percentage (%)", helper: "Total profit as % of machine price" }
      ],
      requiredRole: "ADMIN",
    }
  ];

  const filteredFieldGroups = fieldGroups.filter(group => {
    if ((authRole as string) === "ADMIN") {
      return true;
    }
    if (group.requiredRole === null) {
      return true;
    }
    return group.requiredRole === (authRole as string);
  });

  return (
    <div style={{ padding: "16px" }}>
      <Card
        elevation={4}
        sx={(theme) => ({
          borderRadius: "16px",
          background:
            theme.palette.mode === "light"
              ? "rgba(255,255,255,0.75)"
              : "rgba(20,20,20,0.55)",
          backdropFilter: "blur(8px)",
          boxShadow:
            theme.palette.mode === "light"
              ? "0 8px 24px rgba(0,0,0,0.12)"
              : "0 8px 24px rgba(0,0,0,0.45)",
          border: `1px solid ${
            theme.palette.mode === "light"
              ? "rgba(0,0,0,0.06)"
              : "rgba(255,255,255,0.12)"
          }`,
        })}
      >
        <CardHeader
          title="Mining Hosting Price Calculator"
          subheader="Calculate the optimal machine price to offer customers based on your desired profit margin as a hosting provider"
          sx={(theme) => ({
            textAlign: "center",
            "& .MuiCardHeader-title": {
              fontSize: "1.5rem",
              fontWeight: 700,
              fontFamily: "monospace",
              color: theme.palette.primary.dark,
            },
            "& .MuiCardHeader-subheader": {
              opacity: 0.85,
              fontSize: "0.95rem",
              mt: 1,
              color:
                theme.palette.mode === "light"
                  ? theme.palette.grey[700]
                  : theme.palette.grey[300],
            },
          })}
        />

        <CardContent sx={{ mt: 1 }}>
          {/* Info Box */}
          <Box
            sx={(theme) => ({
              mb: 3,
              p: 2,
              borderRadius: "12px",
              textAlign: "center",
              background:
                theme.palette.mode === "light"
                  ? "rgba(13, 59, 113, 0.07)"
                  : "rgba(120,170,255,0.08)",
              border: `1px solid ${
                theme.palette.mode === "light"
                  ? "rgba(13, 59, 113, 0.15)"
                  : "rgba(120,170,255,0.2)"
              }`,
            })}
          >
            <Typography
              variant="body2"
              sx={(theme) => ({
                fontStyle: "italic",
                fontSize: "0.95rem",
                color:
                  theme.palette.mode === "light"
                    ? theme.palette.primary.dark
                    : theme.palette.primary.light,
              })}
            >
              This model calculates the optimal machine price to charge customers
              based on mining profitability, hosting costs, and your desired profit
              margin. Block reward: 3.125 BTC (halving in April 2028).
            </Typography>
          </Box>
          <SearchByBatchModal/>

          {filteredFieldGroups.map((group, groupIndex) => (
            <Box key={group.title} sx={{ mb: groupIndex < filteredFieldGroups.length - 1 ? 4 : 2 }}>
              <Typography
                variant="h6"
                sx={(theme) => ({
                  fontWeight: 700,
                  mb: 1,
                  fontSize: "1.15rem",
                  color: theme.palette.mode === "light" ? theme.palette.primary.dark : theme.palette.primary.light,
                })}
              >
                {group.title}
              </Typography>

              <Typography
                variant="body2"
                sx={(theme) => ({
                  mb: 2,
                  fontSize: "0.85rem",
                  color: theme.palette.mode === "light" ? theme.palette.grey[800] : theme.palette.grey[300],
                })}
              >
                {group.description}
              </Typography>

              {group.title === "Mining Hardware Specifications" ? (
                <Box sx={{ mt: 2 }}>
                  {/* Manual Entry Fields */}
                  <Grid container spacing={2}>
                    {group.fields.map((field: any) => {
                      const isReadOnly = field.adminOnly && (authRole as string) !== "ADMIN";
                      
                      return (
                        <Grid item xs={12} sm={6} md={4} key={field.key}>
                          <TextField
                            fullWidth
                            name={field.key}
                            label={field.label}
                            value={(form as any)[field.key]}
                            onChange={isReadOnly ? undefined : handleChange}
                            type="number"
                            size="small"
                            helperText={field.helper}
                            InputProps={{
                              readOnly: isReadOnly,
                            }}
                            sx={{
                              '& .MuiInputBase-input.Mui-readOnly': {
                                backgroundColor: (theme) => 
                                  theme.palette.mode === 'light' 
                                    ? 'rgba(0, 0, 0, 0.04)' 
                                    : 'rgba(255, 255, 255, 0.08)',
                                cursor: 'not-allowed',
                              },
                            }}
                          />
                        </Grid>
                      );
                    })}
                  </Grid>
                </Box>
              ) : group.fields ? (
                /* Other Groups */
                <Grid container spacing={2}>
                  {group.fields.map((field: any) => {
                   const isReadOnly = field.adminOnly && (authRole as string) !== "ADMIN";
                    
                    return (
                      <Grid item xs={12} sm={6} md={4} key={field.key}>
                        <TextField
                          fullWidth
                          name={field.key}
                          label={field.label}
                          value={(form as any)[field.key]}
                          onChange={isReadOnly ? undefined : handleChange}
                          type="number"
                          size="small"
                          helperText={field.helper}
                          InputProps={{
                            readOnly: isReadOnly,
                          }}
                          sx={{
                            '& .MuiInputBase-input.Mui-readOnly': {
                              backgroundColor: (theme) => 
                                theme.palette.mode === 'light' 
                                  ? 'rgba(0, 0, 0, 0.04)' 
                                  : 'rgba(255, 255, 255, 0.08)',
                              cursor: 'not-allowed',
                            },
                          }}
                        />
                      </Grid>
                    );
                  })}
                </Grid>
              ) : null}

              {groupIndex < filteredFieldGroups.length - 1 && (
                <Divider
                  sx={(theme) => ({
                    mt: 3,
                    mb: 1,
                    borderColor: theme.palette.mode === "light" ? "#cfd8dc" : "rgba(255,255,255,0.15)",
                  })}
                />
              )}
            </Box>
          ))}
        </CardContent>

        <CardActions sx={{ justifyContent: "center", paddingBottom: 3, paddingTop: 1 }}>
          <Button
            variant="contained"
            size="large"
            onClick={handleSubmit}
            disabled={loading}
            sx={{
              width: "260px",
              borderRadius: "12px",
              padding: "12px 24px",
              textTransform: "none",
              fontWeight: 700,
            }}
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : "Calculate Optimal Price"}
          </Button>
        </CardActions>

        {error && (
          <Box my={3} sx={{ px: 2 }}>
            <Alert severity="error" sx={{ borderRadius: "8px" }}>
              {error}
            </Alert>
          </Box>
        )}
      </Card>

      <MiningResultModal open={modalOpen} onClose={() => setModalOpen(false)} result={result} />
      <MachinesResultModal
        open={machineModalOpen}
        onClose={() => setMachineModalOpen(false)}
        title="Mining Results Summary"
        machineData={machineResult}
      />

    </div>
  );
}
